import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeSlash, Envelope, Lock, ArrowLeft, Warning } from '@phosphor-icons/react';
import axios from 'axios';
import { validatePassword, PasswordRequirements } from '../utils/validatePassword';
import Wordmark from '../components/Wordmark';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Password change state
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Shown when the account has no password yet (former Google-only login).
  const [noPasswordMsg, setNoPasswordMsg] = useState(null);

  const { login, refreshAuth } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedPassword = password.trim();
    if (!trimmedEmail || !trimmedPassword) { toast.error('Bitte fülle alle Felder aus'); return; }
    setLoading(true);
    try {
      const userData = await login(trimmedEmail, trimmedPassword);
      if (userData?.must_change_password) {
        setMustChangePassword(true);
        toast.info('Bitte lege ein neues Passwort fest');
      } else {
        toast.success('Willkommen zurück!');
        navigate('/dashboard');
      }
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      let msg;
      if (status === 409) {
        setNoPasswordMsg(typeof detail === 'string' ? detail : null);
        return;
      }
      if (status === 401) {
        msg = 'E-Mail oder Passwort falsch. Bitte überprüfe deine Eingabe.';
      } else if (status === 429) {
        msg = typeof detail === 'string' ? detail : 'Zu viele Versuche. Bitte warte 15 Minuten.';
      } else if (!status) {
        msg = 'Verbindungsfehler. Bitte überprüfe deine Internetverbindung und versuche es erneut.';
      } else {
        msg = typeof detail === 'string' ? detail : 'Anmeldung fehlgeschlagen';
      }
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    const pwdErr = validatePassword(newPassword);
    if (pwdErr) { toast.error(pwdErr); return; }
    if (newPassword !== confirmNewPassword) { toast.error('Passwörter stimmen nicht überein'); return; }
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, { new_password: newPassword }, { withCredentials: true });
      await refreshAuth();
      toast.success('Passwort geändert! Willkommen bei Habla!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Fehler beim Ändern des Passworts');
    } finally { setChangingPassword(false); }
  };

  // Change password screen
  if (mustChangePassword) {
    return (
      <div className="min-h-screen canvas-spain flex items-center justify-center p-6" data-testid="change-password-page">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-1 mb-8">
            <img src="/brand/hca-mark-256.png" alt="Habla" className="w-20 h-20 object-contain" />
            <Wordmark size="md" className="-ml-1" />
          </div>
          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#AB1C24]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Lock size={32} className="text-[#AB1C24]" />
              </div>
              <h2 className="font-heading text-xl font-bold text-[#292524] mb-2">Neues Passwort festlegen</h2>
              <p className="text-[#57534E] text-sm">Wähle ein sicheres Passwort, das du dir gut merken kannst.</p>
            </div>
            <form onSubmit={handleChangePassword} className="space-y-5">
              <div className="space-y-2">
                <Label>Neues Passwort</Label>
                <Input type="password" placeholder="Neues Passwort" value={newPassword}
                  onChange={e => setNewPassword(e.target.value)} className="py-6 rounded-xl" data-testid="new-password-input" />
                <PasswordRequirements password={newPassword} />
              </div>
              <div className="space-y-2">
                <Label>Passwort bestätigen</Label>
                <Input type="password" placeholder="Passwort wiederholen" value={confirmNewPassword}
                  onChange={e => setConfirmNewPassword(e.target.value)} className="py-6 rounded-xl" data-testid="confirm-new-password-input" />
              </div>
              <Button type="submit" disabled={changingPassword} className="w-full py-6 rounded-xl bg-[#AB1C24] hover:bg-[#8A151C]" data-testid="change-password-btn">
                {changingPassword ? 'Wird gespeichert...' : 'Passwort speichern'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Login screen
  return (
    <div className="min-h-screen canvas-spain flex" data-testid="auth-page">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-[#AB1C24] to-[#8A151C] items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-40 h-40 rounded-full border-2 border-white/30" />
          <div className="absolute bottom-32 right-20 w-60 h-60 rounded-full border-2 border-white/20" />
        </div>
        <div className="text-white text-center max-w-lg relative z-10">
          <img src="/brand/hca-mark-512.png" alt="Habla" className="w-40 h-40 object-contain mx-auto mb-6 drop-shadow-2xl" />
          <Wordmark size="xl" tone="light" className="mb-3 justify-center" />
          <p className="text-white/80 text-lg mb-10">
            Dein Weg zum fließenden Spanisch — mit echtem Unterricht und intelligenter Technologie.
          </p>
          <div className="space-y-3 text-left max-w-sm mx-auto">
            {[
              'Wöchentlicher Live-Kurs mit Alberto',
              'KI-Lehrerin für tägliches Üben',
              'Datengetriebene Lernanalyse',
              'Community & Buddy-System',
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3">
                <div className="w-2 h-2 rounded-full bg-[#EAB308] flex-shrink-0" />
                <span className="text-white/90 text-sm">{item}</span>
              </div>
            ))}
          </div>
          <div className="mt-10 bg-white/10 backdrop-blur-sm rounded-xl p-5">
            <p className="text-white/90 italic">"El que persevera, alcanza."</p>
            <p className="text-white/60 mt-1 text-sm">— Wer durchhält, erreicht sein Ziel.</p>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Back to home — subtle top-left, stays readable on hover */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#57534E] hover:text-[#AB1C24] hover:bg-[#FDF0ED] focus:text-[#AB1C24] focus:bg-[#FDF0ED] rounded-lg px-3 py-2 -ml-3 mb-6 transition-colors"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft size={16} weight="bold" />
            <span>Zurück zur Startseite</span>
          </Link>

          <Link to="/" className="flex items-center gap-1 mb-10" data-testid="logo-link">
            <img src="/brand/hca-mark-256.png" alt="Habla" className="w-20 h-20 object-contain" />
            <Wordmark size="md" className="-ml-1" />
          </Link>

          <h1 className="font-heading text-2xl font-bold text-[#292524] mb-2">Anmelden</h1>
          <p className="text-[#57534E] mb-8">Gib deine Zugangsdaten ein, um fortzufahren.</p>

          {noPasswordMsg && (
            <div
              className="mb-6 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 text-sm flex items-start gap-2"
              data-testid="no-password-notice"
            >
              <Warning size={18} weight="duotone" className="flex-shrink-0 mt-0.5" />
              <span>
                {noPasswordMsg}{' '}
                <Link to="/forgot-password" className="font-semibold underline" data-testid="no-password-reset-link">Passwort jetzt erstellen</Link>
              </span>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#292524]">E-Mail</Label>
              <div className="relative">
                <Envelope size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#78716C]" />
                <Input id="email" type="email" placeholder="deine@email.de" value={email} onChange={e => setEmail(e.target.value)}
                  className="pl-12 py-6 rounded-xl border-[#D6D3D1]" data-testid="email-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#292524]">Passwort</Label>
              <div className="relative">
                <Lock size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#78716C]" />
                <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="Dein Passwort" value={password} onChange={e => setPassword(e.target.value)}
                  className="pl-12 pr-12 py-6 rounded-xl border-[#D6D3D1]" data-testid="password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#78716C] hover:text-[#292524]">
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-sm text-[#AB1C24] hover:underline" data-testid="forgot-password-link">Passwort vergessen?</Link>
            </div>
            <Button type="submit" disabled={loading} className="w-full py-6 rounded-xl bg-[#AB1C24] hover:bg-[#8A151C] btn-press" data-testid="login-submit-btn">
              {loading ? 'Anmelden...' : 'Anmelden'}
            </Button>
          </form>

          <p className="text-center mt-8 text-sm text-[#78716C]">
            Noch kein Konto? <Link to="/" className="text-[#AB1C24] font-semibold hover:underline">Jetzt auf der Startseite abonnieren</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
